import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useProject } from '../../contexts/ProjectContext';
import { dashboardService } from '../../services/dashboard';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatCost(original, actual) {
  const orig = Number(original || 0);
  const act = Number(actual || 0);
  if (!orig && !act) return '$0.00';
  if (Math.abs(orig - act) < 0.01) return `$${act.toFixed(2)}`;
  return `$${orig.toFixed(2)} → $${act.toFixed(2)}`;
}

export default function ApiKeysTab() {
  const navigate = useNavigate();
  const { projects } = useProject();
  const [keys, setKeys] = useState([]);
  const [projectFilter, setProjectFilter] = useState('');
  const [keyToDelete, setKeyToDelete] = useState(null);

  const load = () => dashboardService.keys(projectFilter || null, (res) => setKeys(res.data), console.error);

  useEffect(() => { load(); }, [projectFilter]);

  const confirmDeleteKey = () => {
    if (!keyToDelete) return;
    dashboardService.revokeKey(keyToDelete.hash, () => {
      setKeys((prev) => prev.map((k) => (
        k.hash === keyToDelete.hash ? { ...k, is_active: false } : k
      )));
      setKeyToDelete(null);
    }, console.error);
  };

  return (
    <div>
      <p className="text-sm opacity-70 mb-6">
        API keys authenticate API requests. Send as <code className="text-theme-primary text-xs">Authorization: Bearer osr_…</code> or set <code className="text-theme-primary text-xs">OPENAI_API_KEY</code> when using the OpenAI SDK.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <button
          type="button"
          onClick={() => navigate('/settings/keys/new/')}
          className="px-4 py-2 rounded-lg bg-theme-primary text-theme-button text-sm font-medium"
        >
          Create key
        </button>
        <select
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
          className="rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-sm"
        >
          <option value="">All projects</option>
          {projects.map((p) => (
            <option key={p.hash} value={p.hash}>{p.name}</option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-theme-secondary bg-theme-main">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-theme-secondary bg-theme-secondary/30 text-left">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Project</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3 font-medium">Last used</th>
              <th className="px-4 py-3 font-medium">Cost (30d)</th>
              <th className="px-4 py-3 font-medium">Savings (30d)</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.hash} className="border-b border-theme-secondary/50 last:border-0 hover:bg-theme-secondary/10 transition-colors">
                <td className="px-4 py-3">
                  <div className="font-medium">{k.name}</div>
                  <div className="font-mono text-xs opacity-40 mt-0.5">{k.token_prefix}…</div>
                </td>
                <td className="px-4 py-3">{k.project?.name || '—'}</td>
                <td className="px-4 py-3 text-xs opacity-70">{formatDate(k.create_date)}</td>
                <td className="px-4 py-3 text-xs opacity-70">{formatDate(k.last_used_at)}</td>
                <td className="px-4 py-3 font-mono text-xs">
                  {formatCost(k.usage_30d?.original_cost_usd, k.usage_30d?.actual_cost_usd)}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-emerald-400">
                  ${(k.usage_30d?.savings_usd || 0).toFixed(2)}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                    k.is_active
                      ? 'bg-emerald-500/15 text-emerald-400'
                      : 'bg-theme-secondary opacity-50'
                  }`}
                  >
                    {k.is_active ? 'Active' : 'Revoked'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {k.is_active && (
                    <button
                      type="button"
                      onClick={() => setKeyToDelete(k)}
                      className="text-sm opacity-50 hover:opacity-100 hover:text-red-400 transition-colors"
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {keys.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center opacity-50">
                  No API keys yet. Create one to connect your app.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {keyToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-theme-main border border-theme-secondary p-6 shadow-lg">
            <h2 className="text-lg font-medium mb-2">Revoke API key?</h2>
            <p className="text-sm opacity-70 mb-6">
              <span className="font-medium">{keyToDelete.name}</span>
              {' '}
              (<span className="font-mono">{keyToDelete.token_prefix}…</span>) will stop working immediately.
            </p>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setKeyToDelete(null)} className="px-4 py-2 rounded-lg border border-theme-secondary text-sm">
                Cancel
              </button>
              <button type="button" onClick={confirmDeleteKey} className="px-4 py-2 rounded-lg bg-theme-primary text-theme-button text-sm font-medium">
                Revoke key
              </button>
            </div>
          </div>
        </div>
      )}

      <Outlet context={{ reloadKeys: load }} />
    </div>
  );
}
